import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/client";
import { 
  ArrowUpIcon, 
  ArrowDownIcon, 
  ArrowRightIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";
import { displayNameFor } from "../App";
import { openStockInNewTab } from "../utils/openStock";

// DailySignalsTable now focuses solely on individual assets

export default function DailySignalsTable({ signals = [], theme = 'light', showAggregate = false, showStocks = true, initialSelectedMarket = 'NASDAQ', initialSectorFilter = '' }) {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState('symbol');
  const [sortDirection, setSortDirection] = useState('asc');
  // Context menu for symbol table
  const stocksContainerRef = useRef(null);
  const menuRef = useRef(null);
  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0, item: null });
  const [filterText, setFilterText] = useState(initialSectorFilter || '');
  // Support selecting multiple markets/categories. initialSelectedMarket may be a string, a comma-separated list, 'USALL', or undefined.
  const normalizeInitial = () => {
    try {
      if (!initialSelectedMarket) return ['NASDAQ'];
      if (Array.isArray(initialSelectedMarket)) return initialSelectedMarket.map(s => String(s).toUpperCase());
      const s = String(initialSelectedMarket).toUpperCase();
      if (!s) return ['NASDAQ'];
      if (s.includes(',')) return s.split(',').map(x => x.trim()).filter(Boolean);
      return [s];
    } catch {
      return ['NASDAQ'];
    }
  };
  const [selectedMarkets, setSelectedMarkets] = useState(() => normalizeInitial());
  const [marketStocks, setMarketStocks] = useState([]);
  const [marketLoading, setMarketLoading] = useState(false);

  const markets = ['SP500', 'DJI', 'NASDAQ', 'NYSE', 'USALL', 'CRYPTO', 'ETF', 'FOREX', 'COMMODITIES', 'BIST'];

  // Helper: primary selected market (fallback) and display string for UI
  const primarySelectedMarket = (selectedMarkets && selectedMarkets.length) ? selectedMarkets[0] : 'NASDAQ';
  const displaySelectedMarkets = (selectedMarkets && selectedMarkets.length) ? selectedMarkets.map(m => displayNameFor(m)).join(', ') : '';
  const selectedMarketsLabel = (selectedMarkets && selectedMarkets.length) ? selectedMarkets.map(m => displayNameFor(m)).join(', ') : '';

  // Keep local state in sync when parent/navigation supplies initial values
  useEffect(() => {
    // Keep local state in sync when parent/navigation supplies initial values
    setSelectedMarkets(normalizeInitial());
  }, [initialSelectedMarket]);

  useEffect(() => {
    if (initialSectorFilter) {
      setFilterText(initialSectorFilter);
    }
  }, [initialSectorFilter]);

  // Visual separators between groups
  const predictionsDividerCls = `${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'} border-l-2`;
 
  // Use market-specific data when available, otherwise fall back to filtered signals
  const individualSignals = marketStocks.length > 0 ? marketStocks : 
    signals.filter(s => {
      const signalMarket = (s.market || s.category || s.market_category || '').toString().toUpperCase();
      return selectedMarkets.includes(signalMarket);
    });

  // Fetch market-specific assets when selected markets change
  useEffect(() => {
    let isMounted = true;
    const fetchMarketStocks = async () => {
      // If no categories selected, clear and do not fetch
      if (!selectedMarkets || selectedMarkets.length === 0) {
        if (isMounted) setMarketStocks([]);
        return;
      }

      if (isMounted) setMarketLoading(true);
      try {
        // Helper: fetch items for a single category with graceful fallbacks
        const fetchByCategory = async (cat) => {
          const catUp = String(cat || '').toUpperCase();
          // 1) Preferred: overview/items (fast and enriched)
          try {
            const r = await apiClient.get('/market/overview/items', { params: { category: catUp } });
            const arr = Array.isArray(r.data) ? r.data : [];
            if (arr && arr.length) {
              return arr.map((d) => ({ ...d, __sourceMarket: catUp }));
            }
          } catch (err) {
            // If 404 or any error, fall through to fallback
          }
          // 2) Fallback: legacy predictions list for the market
          try {
            const r2 = await apiClient.get('/market/stocks', { params: { market: catUp, limit: 200 } });
            const arr2 = Array.isArray(r2.data) ? r2.data : [];
            return arr2.map((d) => ({ ...d, __sourceMarket: catUp }));
          } catch (err2) {
            return [];
          }
        };

        // If multiple categories are selected (e.g., USALL), fetch in parallel per category
        let combined = [];
        if (selectedMarkets.length > 1) {
          const results = await Promise.all(selectedMarkets.map((cat) => fetchByCategory(cat)));
          combined = results.flat();
        } else {
          combined = await fetchByCategory(selectedMarkets[0]);
        }

        // Normalize returned items to include an explicit market tag and dedupe by symbol+market
        const seen = new Set();
        const marketData = (Array.isArray(combined) ? combined : []).map((stock) => {
          const mkt = (stock.market || stock.category || stock.market_category || stock.__sourceMarket || '').toString().toUpperCase();
          return { ...stock, market: mkt };
        }).filter((s) => {
          const key = (s.symbol || '').toString().toUpperCase();
          if (!s.symbol) return false;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        if (isMounted) setMarketStocks(marketData);
      } catch (e) {
        console.error(`Failed to fetch ${selectedMarkets.join(',')} stocks:`, e);
        if (isMounted) setMarketStocks([]);
      } finally {
        if (isMounted) setMarketLoading(false);
      }
    };

    fetchMarketStocks();
    return () => { isMounted = false; };
  }, [selectedMarkets]);

  const formatCET = (value) => {
    try {
      if (!value) return "-";
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        const dateStr = new Intl.DateTimeFormat("en-US", { 
          month: "short", 
          day: "2-digit", 
          timeZone: "Europe/Berlin" 
        }).format(d);
        const timeStr = new Intl.DateTimeFormat("en-US", { 
          hour: "2-digit", 
          minute: "2-digit", 
          hour12: false, 
          timeZone: "Europe/Berlin" 
        }).format(d);
        return `${dateStr}, ${timeStr}`;
      }
      // Fallback for strings like "September 01, 03:15" or legacy format
      const m = String(value).match(/^([A-Za-z]+)\s+(\d{1,2}),?\s*(?:\d{4})?\s*(?:at\s*)?(\d{2}):(\d{2})/);
      if (m) {
        const full = m[1];
        const day = String(Number(m[2]));
        const hh = m[3];
        const mm = m[4];
        const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
        const idx = months.findIndex((x) => x.toLowerCase() === full.toLowerCase());
        const short = idx >= 0 ? ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][idx] : full.slice(0,3);
        return `${short} ${day}, ${hh}:${mm}`;
      }
      return String(value);
    } catch {
      return "-";
    }
  };

  const renderPrediction = (prediction) => {
    const base = 'inline-flex items-center gap-1 font-medium';
    const p = String(prediction || '').trim().toUpperCase();

    // If prediction is missing or explicitly N/A, show N/A (no direction icon)
    if (!p || p === 'N/A' || p === 'NA' || p === '-') {
      return (
        <span className={`${base} text-gray-500 dark:text-gray-400`}>N/A</span>
      );
    }

    if (p === 'UPWARD' || p === 'UP') {
      return (
        <span className={`${base} text-green-600 dark:text-green-400`}>
          <ArrowUpIcon className="h-4 w-4" /> Upward
        </span>
      );
    }
    if (p === 'DOWNWARD' || p === 'DOWN') {
      return (
        <span className={`${base} text-red-600 dark:text-red-400`}>
          <ArrowDownIcon className="h-4 w-4" /> Downward
        </span>
      );
    }
    // For explicit neutral/sideways predictions, keep a neutral indicator
    if (p === 'SIDEWAYS' || p === 'NEUTRAL') {
      return (
        <span className={`${base} text-gray-600 dark:text-gray-400`}>
          <ArrowRightIcon className="h-4 w-4" /> Neutral
        </span>
      );
    }
    // Unknown value, display as text safely
    return <span className={`${base} text-gray-600 dark:text-gray-400`}>{p}</span>;
  };

  // Removed aggregate parsing/count helpers

  const formatNumber = (value) => {
    if (value == null || value === '') return '-';
    const num = Number(value);
    return isNaN(num) ? '-' : num.toFixed(2);
  };

  const getModelSignal = (item, modelName) => {
    if (!item) return '';
    return (
      item[`${modelName}_signal`] ||
      item[modelName] ||
      item?.model_stats?.current_signals?.[`${modelName}_signal`] ||
      ''
    );
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronUpIcon className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUpIcon className="h-4 w-4 text-blue-600" />
      : <ChevronDownIcon className="h-4 w-4 text-blue-600" />;
  };

  // Removed aggregate sorting helpers

  const getSortValue = (item, field) => {
    switch (field) {
      case 'symbol':
        return item.symbol || '';
      case 'model1_signal':
        return getModelSignal(item, 'model1') || '';
      case 'model2_signal':
        return getModelSignal(item, 'model2') || '';
      case 'model_ensemble_signal':
        return getModelSignal(item, 'model_ensemble') || '';
      case 'last_updated':
        return new Date(item.last_updated || item.last_update_time || 0).getTime();
      case 'close_price':
        return Number(item.current_price || item.last_close_price || item.close || 0);
      case 'gics_sector':
        return item.gics_sector || '';
      case 'consolidation_score':
        return Number(item.consolidation_score || 0);
      case 'trending_score':
        return Number(item.trending_score || 0);
      case 'bullish_score':
        return Number(item.bullish_score || item.bullish_percentage || item.bullish || 0);
      case 'bearish_score':
        return Number(item.bearish_score || item.bearish_percentage || item.bearish || 0);
      default:
        return '';
    }
  };

  const filteredAndSortedSignals = useMemo(() => {
    let filtered = individualSignals;
    
      // Filter by search text
      if (filterText.trim()) {
        const searchTerm = filterText.toLowerCase().trim();
        filtered = individualSignals.filter(signal => 
          (signal.symbol || '').toLowerCase().includes(searchTerm) ||
          (signal.gics_sector || '').toLowerCase().includes(searchTerm) ||
          getModelSignal(signal, 'model1').toLowerCase().includes(searchTerm) ||
          getModelSignal(signal, 'model2').toLowerCase().includes(searchTerm) ||
          getModelSignal(signal, 'model_ensemble').toLowerCase().includes(searchTerm)
        );
      }    // Sort
    return [...filtered].sort((a, b) => {
      const aVal = getSortValue(a, sortField);
      const bVal = getSortValue(b, sortField);
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
  }, [individualSignals, filterText, sortField, sortDirection]);

  // Close context menu on outside click, ESC, or scroll
  useEffect(() => {
    if (!ctxMenu.open) return;
    const onDown = (e) => {
      if (!menuRef.current) return setCtxMenu({ open: false, x: 0, y: 0, item: null });
      if (!menuRef.current.contains(e.target)) {
        setCtxMenu({ open: false, x: 0, y: 0, item: null });
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setCtxMenu({ open: false, x: 0, y: 0, item: null }); };
    const onScroll = () => setCtxMenu({ open: false, x: 0, y: 0, item: null });
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [ctxMenu.open]);

  const marketForSignal = (signal) =>
    signal.market || signal.category || signal.market_category || primarySelectedMarket;

  const handleStockClick = (signal) => {
    if (signal.symbol) {
      const market = marketForSignal(signal);
      navigate(`/summary?stock=${encodeURIComponent(signal.symbol)}&market=${encodeURIComponent(String(market).toUpperCase())}`);
    }
  };

  const SortableHeader = ({ field, children, className = '', thProps = {} }) => (
    <th
      {...thProps}
      className={`px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-[75%] text-center ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center justify-center gap-1">
        <span>{children}</span>
        {getSortIcon(field)}
      </div>
    </th>
  );

  return (
    <div className="space-y-6">
      {/* Individual Stocks Section */}
      {showStocks && (
      <div ref={stocksContainerRef} className="relative">
  <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Daily Asset Outlook</h2>
        
        {/* Market Filter Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          {markets.map((market) => (
            <button
              key={market}
              onClick={() => {
                // Toggle presence in selectedMarkets to allow multi-select
                setSelectedMarkets(prev => {
                  const up = String(market).toUpperCase();
                  if (prev.includes(up)) return prev.filter(x => x !== up);
                  return [...prev, up];
                });
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                selectedMarkets.includes(market)
                  ? 'bg-blue-600 text-white'
          : theme === 'dark'
            ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            : 'bg-gray-300 text-gray-800 hover:bg-gray-400'
              }`}
            >
              {displayNameFor(market)}
            </button>
          ))}
        </div>
        
        {/* Filter Box */}
      <div className="flex items-center space-x-4 mb-4">
        <div className="flex-1">
          <div className="relative">
            <input
              type="text"
              placeholder="Filter assets by symbol, sector, or prediction..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${filterText.trim() ? 'bg-blue-50 dark:bg-blue-900/40 border-blue-200 dark:border-blue-800 text-gray-900 dark:text-white' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white'} placeholder-gray-500 dark:placeholder-gray-400`}
            />
            {filterText.trim() && (
              <button
                type="button"
                onClick={() => setFilterText('')}
                aria-label="Clear filter"
                className="absolute right-2 inset-y-0 m-auto text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white flex items-center justify-center h-8 w-8 leading-none"
              >
                {/* simple X icon */}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 8.586L15.293 3.293a1 1 0 111.414 1.414L11.414 10l5.293 5.293a1 1 0 01-1.414 1.414L10 11.414l-5.293 5.293a1 1 0 01-1.414-1.414L8.586 10 3.293 4.707A1 1 0 014.707 3.293L10 8.586z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {marketLoading ? (
            <span>{selectedMarketsLabel ? `Loading ${selectedMarketsLabel} assets...` : 'Loading assets...'}</span>
          ) : (
            <span>
              {filteredAndSortedSignals.length} of {individualSignals.length}{' '}
              {selectedMarketsLabel ? `${selectedMarketsLabel} assets` : 'assets'}
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-hidden border rounded-lg border-gray-200 dark:border-gray-700">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th colSpan={5} className="px-3 py-2 text-center text-[85%] font-bold">Asset Overview</th>
              <th colSpan={6} className={`px-3 py-2 text-center text-[85%] font-bold ${predictionsDividerCls}`}>AI Predictions</th>
            </tr>
            <tr>
              <SortableHeader field="symbol">Symbol</SortableHeader>
              <SortableHeader field="gics_sector">Sector</SortableHeader>
              <SortableHeader field="close_price">Close Price ($)</SortableHeader>
              <SortableHeader field="consolidation_score">Cons. Score</SortableHeader>
              <SortableHeader field="trending_score">Trending Score</SortableHeader>
              <SortableHeader field="model1_signal" className={predictionsDividerCls}>Signal (Model1)</SortableHeader>
              <SortableHeader field="model2_signal">Signal (Model2)</SortableHeader>
              <SortableHeader field="model_ensemble_signal">Signal (Ensemble)</SortableHeader>
              <SortableHeader field="bullish_score">Bullish Score</SortableHeader>
              <SortableHeader field="bearish_score">Bearish Score</SortableHeader>
              <SortableHeader field="last_updated">Updated (CET)</SortableHeader>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {marketLoading && (
              <tr>
                <td colSpan={11} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2">Loading {displaySelectedMarkets} assets...</span>
                  </div>
                </td>
              </tr>
            )}
            {!marketLoading && filteredAndSortedSignals.length === 0 && (
              <tr>
                <td colSpan={11} className="px-4 py-6 text-center text-gray-500 dark:text-gray-400">
                  {filterText.trim()
                    ? 'No aassets match your filter.'
                    : (selectedMarkets && selectedMarkets.length === 0 ? 'No categories is selected.' : `No ${displaySelectedMarkets} assets available.`)}
                </td>
              </tr>
            )}
            {filteredAndSortedSignals.map((signal, index) => {
              const isDailyPick = Boolean(
                signal?.is_daily_pick ||
                signal?.daily_pick ||
                signal?.isDailyPick ||
                signal?.dailyPick ||
                signal?.pick_of_day ||
                signal?.pickOfDay
              );
              return (
                <tr 
                  key={signal.symbol || signal.id || index} 
                  className="hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <td 
                    className={`px-3 py-2 font-medium text-blue-600 dark:text-blue-400 cursor-pointer hover:underline ${isDailyPick ? 'bg-gray-100 dark:bg-gray-800' : ''}`}
                    title={signal.name || signal.company_name || signal.symbol || ''}
                    aria-label={(signal.name || signal.company_name || signal.symbol || '').toString()}
                    onClick={(e) => {
                      if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        openStockInNewTab(signal.symbol, marketForSignal(signal));
                        return;
                      }
                      handleStockClick(signal);
                    }}
                    onAuxClick={(e) => {
                      if (e.button === 1) {
                        e.preventDefault();
                        openStockInNewTab(signal.symbol, marketForSignal(signal));
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      try {
                        const rect = stocksContainerRef.current ? stocksContainerRef.current.getBoundingClientRect() : { left: 0, top: 0 };
                        const x = e.clientX - rect.left;
                        const y = e.clientY - rect.top;
                        const market = signal.market || signal.category || signal.market_category || primarySelectedMarket;
                        setCtxMenu({ open: true, x, y, item: { symbol: signal.symbol, market: String(market).toUpperCase() } });
                      } catch {
                        const market = signal.market || signal.category || signal.market_category || primarySelectedMarket;
                        setCtxMenu({ open: true, x: e.clientX, y: e.clientY, item: { symbol: signal.symbol, market: String(market).toUpperCase() } });
                      }
                    }}
                  >
                    <div className="flex items-center gap-1">
                      <span
                        className="truncate"
                        title={signal.name || signal.company_name || signal.symbol || ''}
                        aria-label={(signal.name || signal.company_name || signal.symbol || '').toString()}
                      >
                        {signal.symbol || '-'}
                      </span>
                      {isDailyPick && <StarIcon className="h-4 w-4 text-yellow-500" aria-label="Daily pick" />}
                    </div>
                  </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs leading-tight">
                  <span className="block max-w-[10rem] truncate">
                    {signal.gics_sector || '-'}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {signal.current_price != null
                    ? `$${formatNumber(signal.current_price)}`
                    : (signal.last_close_price != null
                        ? `${formatNumber(signal.last_close_price)}`
                        : (signal.close != null ? `${formatNumber(signal.close)}` : '-'))}
                </td>
                <td className="px-3 py-2">
                  {formatNumber(signal.consolidation_score)}
                </td>
                <td className="px-3 py-2">
                  {formatNumber(signal.trending_score)}
                </td>
                <td className="px-3 py-2">
                  {renderPrediction(getModelSignal(signal, 'model1'))}
                </td>
                <td className="px-3 py-2">
                  {renderPrediction(getModelSignal(signal, 'model2'))}
                </td>
                <td className="px-3 py-2">
                  {renderPrediction(getModelSignal(signal, 'model_ensemble'))}
                </td>
                <td className="px-3 py-2">
                  {formatNumber(signal.bullish_score || signal.bullish_percentage || signal.bullish)}
                </td>
                <td className="px-3 py-2">
                  {formatNumber(signal.bearish_score || signal.bearish_percentage || signal.bearish)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs leading-tight">
                  <span className="block max-w-[12rem] truncate">
                    {formatCET(signal.last_updated || signal.last_update_time)}
                  </span>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {/* Context Menu */}
        {ctxMenu.open && ctxMenu.item && (
          <div
            ref={menuRef}
            className="absolute z-50 min-w-[180px] rounded-md border shadow-lg text-sm bg-white dark:bg-gray-800 dark:border-gray-700"
            style={{
              left: Math.min(
                ctxMenu.x,
                (stocksContainerRef.current ? stocksContainerRef.current.clientWidth : 240) - 200
              ),
              top: Math.min(
                ctxMenu.y,
                (stocksContainerRef.current ? stocksContainerRef.current.clientHeight : 400) - 100
              ),
            }}
            role="menu"
          >
            <button
              type="button"
              className="block w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => {
                try {
                  const url = `${window.location.origin}/summary?stock=${encodeURIComponent(ctxMenu.item.symbol)}&market=${encodeURIComponent(ctxMenu.item.market)}`;
                  window.open(url, '_blank', 'noopener');
                } catch {}
                setCtxMenu({ open: false, x: 0, y: 0, item: null });
              }}
              role="menuitem"
            >
              Open in New Tab
            </button>
            <button
              type="button"
              className="block w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => {
                try {
                  const url = `${window.location.origin}/summary?stock=${encodeURIComponent(ctxMenu.item.symbol)}&market=${encodeURIComponent(ctxMenu.item.market)}`;
                  window.open(url, '_blank', 'noopener,width=1200,height=800');
                } catch {}
                setCtxMenu({ open: false, x: 0, y: 0, item: null });
              }}
              role="menuitem"
            >
              Open in New Window
            </button>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
